import "./style.css";
import { useEffect, useState, useCallback } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { AgGridReact } from "ag-grid-react";
import { Link } from "react-router-dom";
import debounce from "lodash/debounce";
import { Col, Row, Form } from "react-bootstrap";
import Widgets from "../../../components/Widgets";
import { useGlobalToast } from '../../../stopOldToastGlobally.js';
import {
  commonGetData,
  commonSaveData,
  initCommonState,
  initState,
} from "../../../redux/slices/commonSlice";
import {
  getEmployeeSalaryListPath,
  deactivateEmployeePath,
} from "../../../services/config";
import { LiaRupeeSignSolid } from "react-icons/lia";
import CustomPagination from "../../../components/CustomPagination";
import { FiDownload } from "react-icons/fi";
import { PiPencilSimpleLineBold } from "react-icons/pi";
import { MdClear } from 'react-icons/md';
import Button from "@mui/material/Button";
import {
  getCurrentDate,
  getInitials,
  getInitialColor,
} from "../../../services/commonServices";
import { HasPermission } from "../../../services/hasPermission";
import TableSkeleton from "../../../components/table/TableSkeleton.js";

// ============================================================
// SALARY DOWNLOAD HEADERS (exact order from document)
// ============================================================
const SALARY_HEADERS = [
  "Sl No",
  "Employee ID",
  "Employee Name",
  "Department",
  "Designation",
  "Branch",
  "Employment Status",
  "Payroll Cycle",
  "Wage Type",
  "Basic",
  "Dearness Allowance",
  "House Rent Allowance",
  "Other Allowance",
  "Gross Salary",
  "Employee PF Contribution",
  "Employee ESI Contribution",
  "Employee LWF",
  "Professional Tax",
  "TDS Deduction",
  "Net Salary",
  "Employer PF Contribution",
  "EDLI",
  "Employer ESIC Contribution",
  "Insurance",
  "Cost to Company",
];

// Map raw salary row → flat object matching SALARY_HEADERS
const mapSalaryRow = (item, index) => {
  const sb = item.salaryBreakup || {};

  // Dearness Allowance from allowanceList
  const da = sb.allowanceList?.find((a) => a.name === "Dearness Allowance");
  const daAmount = da?.monthlyAmount ? parseFloat(da.monthlyAmount).toFixed(2) : "0.00";

  // Special/Other allowances
  const otherAllowance = sb.otherAllowances
    ? parseFloat(sb.otherAllowances).toFixed(2)
    : "0.00";

  // Employer Allowance (newAllowanceList)
  const insurance = sb.newAllowanceList
    ? sb.newAllowanceList.reduce((t, a) => t + parseFloat(a.monthlyAmount || 0), 0).toFixed(2)
    : "0.00";

  return {
    "Sl No":                       index + 1,
    "Employee ID":                 item.employee_id     || "-",
    "Employee Name":               item.full_name        || "-",
    "Department":                  item.departmentName   || item.department || "-",
    "Designation":                 item.job_title        || "-",
    "Branch":                      item.branchNames      || item.branchName || "-",
    "Employment Status":           item.status === "1" ? "Active" : "Inactive",
    "Payroll Cycle":               item.payroll_cycle    || "-",
    "Wage Type":                   item.wage_type        || "-",
    "Basic":                       sb.basicPay
                                     ? parseFloat(sb.basicPay).toFixed(2)
                                     : "0.00",
    "Dearness Allowance":          daAmount,
    "House Rent Allowance":        sb.hraPay
                                     ? parseFloat(sb.hraPay).toFixed(2)
                                     : "0.00",
    "Other Allowance":             otherAllowance,
    "Gross Salary":                sb.grossSalary
                                     ? parseFloat(sb.grossSalary).toFixed(2)
                                     : "0.00",
    "Employee PF Contribution":    sb.employeePf
                                     ? parseFloat(sb.employeePf).toFixed(2)
                                     : "0.00",
    "Employee ESI Contribution":   sb.employeeEsi
                                     ? parseFloat(sb.employeeEsi).toFixed(2)
                                     : "0.00",
    "Employee LWF":                sb.lwfAmount
                                     ? parseFloat(sb.lwfAmount).toFixed(2)
                                     : "0.00",
    "Professional Tax":            sb.professionalTax
                                     ? parseFloat(sb.professionalTax).toFixed(2)
                                     : "0.00",
    "TDS Deduction":               sb.tdsAmount
                                     ? parseFloat(sb.tdsAmount).toFixed(2)
                                     : "0.00",
    "Net Salary":                  sb.netSalary
                                     ? parseFloat(sb.netSalary).toFixed(2)
                                     : "0.00",
    "Employer PF Contribution":    sb.employerPf
                                     ? parseFloat(sb.employerPf).toFixed(2)
                                     : "0.00",
    "EDLI":                        sb.employerEdli
                                     ? parseFloat(sb.employerEdli).toFixed(2)
                                     : "0.00",
    "Employer ESIC Contribution":  sb.employerEsi
                                     ? parseFloat(sb.employerEsi).toFixed(2)
                                     : "0.00",
    "Insurance":                   insurance,
    "Cost to Company":             sb.totalEmployerContributions
                                     ? parseFloat(sb.totalEmployerContributions).toFixed(2)
                                     : "0.00",
  };
};

// CSV builder
const buildCSV = (rows) => {
  const escape = (val) => {
    const str = String(val ?? "-");
    return str.includes(",") || str.includes('"') || str.includes("\n")
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };
  return [
    SALARY_HEADERS.map(escape).join(","),
    ...rows.map((row) => SALARY_HEADERS.map((h) => escape(row[h])).join(",")),
  ].join("\n");
};

const SalaryList = ({ dispatch, user, state, branchList, designationList, departmentList }) => {
  const customToast = useGlobalToast();
  const [usersList, setUsersList] = useState([]);
  const [filteredUsersList, setFilteredUsersList] = useState([]);
  const [filterUser, setFilterUser] = useState("");

  const [jobTitle, setJobTitle] = useState("");
  const [status, setStatus] = useState("");
  const [branch, setBranch] = useState("");
  const [departmentId, setDepartmentId] = useState("");

  const [gridApi, setGridApi] = useState(null);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const path = `${getEmployeeSalaryListPath}${user.user_id
    }&branchId=${branch}&departmentId=${departmentId}&jobTitle=${jobTitle}`;

  const getEmployeeList = () => {
    setUsersList([]);
    setFilteredUsersList([]);
    dispatch(commonGetData(path));
  };

  useEffect(() => {
    getEmployeeList();
  }, [jobTitle, status, branch, departmentId, dispatch, path]);

  useEffect(() => {
    if (state.commonGetSuccess) {
      if (state.commonGetData == undefined || state.commonGetData == "") {
        setUsersList([]);
        setFilteredUsersList([]);
        dispatch(initState());
      } else {
        if (filterUser) {
          handleSearch(filterUser, state.commonGetData.staffDetails || []);
        } else {
          if (state.commonGetData?.staffDetails && state.commonGetData?.staffDetails[0]?.full_name) {
            setUsersList(state.commonGetData.staffDetails || []);
            setFilteredUsersList(state.commonGetData.staffDetails || []);
            dispatch(initState());
          }
        }
      }
    }
    if (state.commonGetError) {
      setUsersList([]);
      setFilteredUsersList([]);
      dispatch(initState());
    }
    if (state.commonSuccess) {
      getEmployeeList();
      dispatch(initCommonState());
    }
    if (state.commonError) {
      dispatch(initCommonState());
    }
  }, [state]);

  const handleActiveInactiveChange = (event, rowData) => {
    const data = {
      path: deactivateEmployeePath,
      data: {
        userId: user.user_id,
        staffId: rowData.staff_id,
        isActiveUser: event.target.value,
      },
    };
    dispatch(commonSaveData(data));
  };

  const columns = [
    {
      headerName: "SNo",
      valueGetter: (params) => params.node.rowIndex + 1,
      width: 60,
      pinned: "left",
    },
    {
      headerName: "Employee ID",
      field: "employee_id",
      width: 120,
      pinned: "left",
    },
    {
      headerName: "Name",
      cellRenderer: (rowData) => {
        return (
          rowData?.data?.staff_id ?
            <Link
              style={{ textDecoration: "none" }}
              to={`user-info/${rowData.data.staff_id}`}
              className="table-link d-flex align-items-center justify-content-start gap-2 color-white"
            >
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  border: "1px solid lightgray",
                  borderRadius: "50%",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: getInitialColor(rowData.data.full_name),
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "12px",
                }}
              >
                {getInitials(rowData.data.full_name)}
              </div>
              {rowData.data.full_name}
            </Link>
            : "-"
        );
      },
      pinned: "left",
      width: "350px",
    },
    {
      headerName: "Mobile Number",
      cellRenderer: (rowData) => {
        return (
          <span>
            {rowData.data.mobileNumber ? rowData.data.mobileNumber : "-"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "130px",
    },
    {
      headerName: "Job Title",
      cellRenderer: (rowData) => {
        return (
          <span>{rowData.data.job_title ? rowData.data.job_title : "-"}</span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "200px",
    },
    {
      headerName: "Branch",
      field: "branchNames",
      width: 200,
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
    },
    {
      headerName: "CTC",
      cellRenderer: (rowData) => {
        return (
          <span>
            <LiaRupeeSignSolid />
            {rowData.data.salaryBreakup?.netSalary
              ? rowData.data.salaryBreakup?.netSalary
              : "0.00"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "150px",
    },
    {
      headerName: "Dearness Allowance",
      cellRenderer: (rowData) => {
        let d = rowData.data.salaryBreakup?.allowanceList?.filter(
          (item) => item.name == "Dearness Allowance"
        )[0];
        return (
          <span>
            <LiaRupeeSignSolid />
            {d?.monthlyAmount ? d?.monthlyAmount : "0.00"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "150px",
    },
    {
      headerName: "HRA",
      cellRenderer: (rowData) => {
        return (
          <span>
            {" "}
            <LiaRupeeSignSolid />
            {rowData.data.salaryBreakup?.hraPay
              ? parseFloat(rowData.data.salaryBreakup?.hraPay).toFixed(2)
              : "0.00"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "150px",
    },
    {
      headerName: "Special Allowance",
      cellRenderer: (rowData) => {
        const totalMonthlyAmount =
          rowData.data.salaryBreakup?.allowanceList?.reduce((total, item) => {
            if (
              item.name !== "Other Allowance" &&
              item.name !== "Dearness Allowance"
            ) {
              return total + parseFloat(item.monthlyAmount);
            }
            return total;
          }, 0);
        return (
          <span>
            <LiaRupeeSignSolid />
            {totalMonthlyAmount ? totalMonthlyAmount.toFixed(2) : "0.00"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "150px",
    },
    {
      headerName: "Other Allowance",
      cellRenderer: (rowData) => {
        return (
          <span>
            {" "}
            <LiaRupeeSignSolid />
            {rowData.data.salaryBreakup?.otherAllowances
              ? rowData.data.salaryBreakup?.otherAllowances
              : "0.00"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "150px",
    },
    {
      headerName: "Employer PF",
      cellRenderer: (rowData) => {
        return (
          <span>
            {" "}
            <LiaRupeeSignSolid />
            {rowData.data.salaryBreakup?.employerPf
              ? parseFloat(rowData.data.salaryBreakup?.employerPf).toFixed(2)
              : "0.00"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "150px",
    },
    {
      headerName: "Employer ESI",
      cellRenderer: (rowData) => {
        return (
          <span>
            {" "}
            <LiaRupeeSignSolid />
            {rowData.data.salaryBreakup?.employerEsi
              ? parseFloat(rowData.data.salaryBreakup?.employerEsi).toFixed(2)
              : "0.00"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "150px",
    },
    {
      headerName: "EDLI & Admin Charges",
      cellRenderer: (rowData) => {
        return (
          <span>
            {" "}
            <LiaRupeeSignSolid />
            {rowData.data.salaryBreakup?.employerEdli
              ? (
                parseFloat(rowData.data.salaryBreakup?.employerEdli) +
                parseFloat(rowData.data.salaryBreakup?.adminCharges)
              ).toFixed(2)
              : "0.00"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "150px",
    },
    {
      headerName: "Employer LWF",
      cellRenderer: (rowData) => {
        return (
          <span>
            {" "}
            <LiaRupeeSignSolid />
            {rowData.data.salaryBreakup?.employerLwfAmount
              ? parseFloat(rowData.data.salaryBreakup?.employerLwfAmount).toFixed(2)
              : "0.00"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "150px",
    },
    {
      headerName: "Total Employer Contribution",
      cellRenderer: (rowData) => {
        return (
          <span>
            {" "}
            <LiaRupeeSignSolid />
            {rowData.data.salaryBreakup?.totalEmployerContributions
              ? parseFloat(rowData.data.salaryBreakup?.totalEmployerContributions).toFixed(2)
              : "0.00"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "150px",
    },
    {
      headerName: "Employee PF",
      cellRenderer: (rowData) => {
        return (
          <span>
            {" "}
            <LiaRupeeSignSolid />
            {rowData.data.salaryBreakup?.employeePf
              ? parseFloat(rowData.data.salaryBreakup?.employeePf).toFixed(2)
              : "0.00"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "150px",
    },
    {
      headerName: "Employee ESI",
      cellRenderer: (rowData) => {
        return (
          <span>
            {" "}
            <LiaRupeeSignSolid />
            {rowData.data.salaryBreakup?.employeeEsi
              ? parseFloat(rowData.data.salaryBreakup?.employeeEsi).toFixed(2)
              : "0.00"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "150px",
    },
    {
      headerName: "Employee LWF",
      cellRenderer: (rowData) => {
        return (
          <span>
            {" "}
            <LiaRupeeSignSolid />
            {rowData.data.salaryBreakup?.lwfAmount
              ? parseFloat(rowData.data.salaryBreakup?.lwfAmount).toFixed(2)
              : "0.00"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "150px",
    },
    {
      headerName: "Professional Tax",
      cellRenderer: (rowData) => {
        return (
          <span>
            {" "}
            <LiaRupeeSignSolid />
            {rowData.data.salaryBreakup?.professionalTax
              ? parseFloat(rowData.data.salaryBreakup?.professionalTax).toFixed(2)
              : "0.00"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "150px",
    },
    {
      headerName: "Total Employee Contribution",
      cellRenderer: (rowData) => {
        return (
          <span>
            {" "}
            <LiaRupeeSignSolid />
            {rowData.data.salaryBreakup?.totalEmployeeContributions
              ? parseFloat(rowData.data.salaryBreakup?.totalEmployeeContributions).toFixed(2)
              : "0.00"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "150px",
    },
    {
      headerName: "Employer Allowance",
      cellRenderer: (rowData) => {
        let totalMonthlyAmount =
          rowData.data.salaryBreakup?.newAllowanceList?.reduce(
            (total, item) => {
              return total + parseFloat(item.monthlyAmount);
            },
            0
          );
        return (
          <span>
            <LiaRupeeSignSolid />
            {totalMonthlyAmount ? totalMonthlyAmount.toFixed(2) : "0.00"}
          </span>
        );
      },
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
      width: "150px",
    },
    {
      headerName: "Action",
      pinned: "right",
      width: "150px",
      cellRenderer: (rowData) => {
        return (
          <div
            style={{ position: "relative", top: "9px" }}
            className="d-flex align-items-center justify-content-start gap-3"
          >
            <select
              className={
                rowData.data.status == "1" ? "statusGreen" : "statusOrange"
              }
              value={rowData.data.status}
              onChange={(e) => handleActiveInactiveChange(e, rowData.data)}
            >
              <option
                value="1"
                selected={
                  rowData.data.status != undefined &&
                  rowData.data.status != null &&
                  rowData.data.status == "1" &&
                  "selected"
                }
                className="option"
              >
                Active
              </option>
              <option
                value="0"
                selected={rowData.data.status != "1"}
                className="option"
              >
                InActive
              </option>
            </select>
            <Link
              to={`user-info/${rowData.data.staffId}`}
              className="table-link d-flex align-items-center justify-content-start gap-2"
              style={{ fontSize: "24px" }}
            >
              <PiPencilSimpleLineBold className="edit-icon" />
            </Link>
          </div>
        );
      },
    },
  ];

  const handleSearch = useCallback(
    debounce((query, dataList) => {
      const debouncedQuery = query.trim().toLowerCase();
      const results = dataList.filter(
        (employee) =>
          (employee.full_name &&
            employee.full_name.toLowerCase().includes(debouncedQuery)) ||
          (employee.job_title &&
            employee.job_title.toLowerCase().includes(debouncedQuery)) ||
          (employee.employee_id &&
            employee.employee_id.toLowerCase().includes(debouncedQuery))
      );
      setFilteredUsersList(results);
      setUsersList(dataList);
    }, 500),
    [usersList]
  );

  const searchUser = (event) => {
    const { target: { value } } = event;
    setFilterUser(value);
    handleSearch(value, usersList);
  };

  // ── Updated download: CSV with correct headers ──
  const downloadBulkData = () => {
    if (!filteredUsersList || filteredUsersList.length === 0) return;

    const rows = filteredUsersList.map((item, idx) => mapSalaryRow(item, idx));
    const csv  = buildCSV(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${getCurrentDate()}_Salary_Detail.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    customToast.success("Salary details downloaded successfully!");
  };

  const paginationPageSize = 10;
  const paginationPageSizeSelector = [10, 20, 50, 100];

  const [statusVal, setStatusVal] = useState("");
  const handleSearchByStatus = useCallback(
    debounce((query) => {
      setStatusVal(query);
      const debouncedQuery = query;
      const results = usersList.filter(
        (employee) => employee.status == debouncedQuery
      );
      setFilteredUsersList(results);
    }, 100),
    [usersList]
  );

  const clearFilter = () => {
    setFilterUser("");
    setJobTitle("");
    setDepartmentId(0);
    setBranch(0);
    setStatusVal("");
    getEmployeeList();
  };

  return (
    <>
      <div className="search-box flex-space-between mt-m-40">
        <Form className="d-flex align-items-center justify-content-start gap-2">
          <Form.Group>
            <Form.Control
              placeholder="Search by Employee Name & ID"
              onChange={searchUser}
              name="filterUser"
              value={filterUser}
              className="universal-input-one"
              style={{ width: "250px" }}
            />
          </Form.Group>

          <Form.Group>
            <Form.Select
              onChange={(e) => setJobTitle(e.target.value)}
              name="jobTitle"
              value={jobTitle}
              className="universal-input-one"
              style={{ width: "200px" }}
            >
              <option value="0">Select Job Title</option>
              {designationList?.map((row) => {
                return <option value={row.label} key={row.label}>{row.label}</option>;
              })}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Select
              onChange={(e) => setBranch(e.target.value)}
              name="branch"
              value={branch}
              className="universal-input-one"
              style={{ width: "200px" }}
            >
              <option value={0}>Select Branch</option>
              {branchList?.map((row) => {
                return <option value={row.branchId} key={row.branchId}>{row.branch_name}</option>;
              })}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Select
              onChange={(e) => setDepartmentId(e.target.value)}
              name="departmentId"
              value={departmentId}
              className="universal-input-one"
              style={{ width: "200px" }}
            >
              <option value="0">Select Department</option>
              {departmentList?.map((row) => {
                return <option value={row.value} key={row.value}>{row.label}</option>;
              })}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Select
              onChange={(e) => handleSearchByStatus(e.target.value)}
              className="universal-input-one"
              name="statusVal"
              value={statusVal}
              style={{ width: "140px" }}
            >
              <option>Select Status</option>
              <option value="1">Active</option>
              <option value="0">InActive</option>
            </Form.Select>
          </Form.Group>

          <Form.Group>
            {
              (filterUser ||
                jobTitle ||
                departmentId != 0 ||
                branch != 0 || statusVal) && <button type="button" className="clear" onClick={clearFilter}>
                <MdClear /> Clear
              </button>
            }
          </Form.Group>
        </Form>
      </div>

      <Widgets
        body={
          <>
            <div
              className="ag-theme-quartz custom-grid"
              style={{ height: "calc(100vh - 310px)", width: "100%" }}
            >
              {state.commonGetLoading ? (
                <TableSkeleton />
              ) : (
                <AgGridReact
                  rowData={Array.isArray(filteredUsersList) ? filteredUsersList : []}
                  columnDefs={columns}
                  headerHeight={null}
                  pagination={true}
                  paginationPageSize={paginationPageSize}
                  paginationPageSizeSelector={paginationPageSizeSelector}
                  onGridReady={onGridReady}
                />
              )}
            </div>
            <div className="d-flex align-items-center justify-content-between custom-pagination">
              {gridApi && <CustomPagination gridApi={gridApi} />}
              {HasPermission("Salary Details", "export") && (
                <Button
                  onClick={downloadBulkData}
                  className="bulkDataDownloads universal-btn"
                >
                  <FiDownload style={{ fontSize: "16px", marginRight: '10px' }} />
                  Export Data
                </Button>
              )}
            </div>
          </>
        }
      />
    </>
  );
};

export default SalaryList;